import { useEffect, useState } from "react";
import {
  fetchDefaultAutoTaggingPlanPrompt,
  type FetchDefaultAutoTaggingPlanPromptOptions,
} from "../services/autoTaggingService";

export interface UseDefaultAssessmentPromptResult {
  defaultPrompt: string;
  loading: boolean;
  error: string | null;
}

export function useDefaultAssessmentPrompt(
  conceptCode: string | undefined,
  options?: FetchDefaultAutoTaggingPlanPromptOptions,
): UseDefaultAssessmentPromptResult {
  const normalizedConceptCode = conceptCode?.trim() ?? "";
  const targetType = options?.targetType?.trim() ?? "";
  const subType = options?.subType?.trim() ?? "";
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!normalizedConceptCode) {
      setDefaultPrompt("");
      setLoading(false);
      setError(null);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    fetchDefaultAutoTaggingPlanPrompt(normalizedConceptCode, {
      targetType,
      subType,
    })
      .then((prompt) => {
        if (!mounted) return;
        setDefaultPrompt(prompt);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setDefaultPrompt("");
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load default assessment prompt.",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [normalizedConceptCode, targetType, subType]);

  return {
    defaultPrompt,
    loading,
    error,
  };
}
